// "Descargar" de Videos cortos (ADR-2026-10-31-videos-cortos-se-descargan-todos): los videos ya salidos, como la
// salida del Catálogo (`SalidasDelCatalogo`), con el mismo botón en el pie y como ícono en el teléfono. Hasta
// `MAX_VIDEOS_EN_ZIP` van en un zip; con más, sueltos. Sin la pregunta de marca de agua y formato: es de imágenes.
// No hay "Lanzamiento": las tiendas (Tiendanube, VTEX, Mercado Libre) no reciben video como foto de producto.
import { Download, Loader2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { descargarComoZip, descargarSueltos, type ArchivoDeLote } from '../../../../utils/descargarLote';
import { firmarOriginales } from '../catalogoAgente/generarCatalogo';
import type { PlanDeVideos } from './plan';
import { comoSeBajan, nombreDelZipDeVideos, videosDelLote } from './videosDelLote';

/** Los mismos que la salida del Catálogo. */
const BOTON =
  'inline-flex items-center gap-2 rounded-xl border border-black/[0.1] bg-white py-3 px-5 text-[15px] font-semibold text-brand-black transition-colors hover:bg-black/[0.03] disabled:cursor-not-allowed disabled:opacity-50';
const BOTON_ICONO =
  'grid h-12 w-12 shrink-0 place-items-center rounded-full bg-white text-brand-black shadow-[0_8px_24px_-10px_rgb(var(--brand-black-rgb)_/_0.28),0_1px_3px_rgb(var(--brand-black-rgb)_/_0.08)] ring-1 ring-black/[0.06] transition-transform active:scale-95 disabled:opacity-50';

export const DescargarLosVideos: React.FC<{
  plan: PlanDeVideos;
  companyId: string | null;
  /** Botón redondo sin texto (el teléfono). */
  soloIconos?: boolean;
}> = ({ plan, companyId, soloIconos = false }) => {
  const videos = useMemo(() => videosDelLote(plan), [plan]);
  const [progreso, setProgreso] = useState<{ hechos: number; total: number } | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  if (videos.length === 0) return null;
  const forma = comoSeBajan(videos.length);
  const titulo = videos.length === 1 ? 'Descargar el video' : forma === 'zip' ? `Descargar los ${videos.length} videos en un zip` : `Descargar los ${videos.length} videos`;

  const descargar = async () => {
    if (progreso || !companyId) return;
    setAviso(null);
    setProgreso({ hechos: 0, total: videos.length });
    try {
      const firmados = await firmarOriginales(
        videos.map((v) => v.imageId),
        companyId,
      );
      const archivos: ArchivoDeLote[] = videos.flatMap((v) => {
        const url = firmados.get(v.imageId);
        return url ? [{ url, nombre: v.nombre, imagenId: v.imageId }] : [];
      });
      const alAvanzar = (hechos: number, total: number) => setProgreso({ hechos, total });
      const r = forma === 'zip' ? await descargarComoZip(archivos, nombreDelZipDeVideos(plan.titulo), alAvanzar) : await descargarSueltos(archivos, alAvanzar);
      const fallidos = r.fallidos + (videos.length - archivos.length);
      if (r.ok === 0) setAviso('No se pudo descargar ninguno. Probá de nuevo.');
      else if (fallidos > 0) setAviso(`Bajé ${r.ok}; ${fallidos} no se pudieron.`);
    } catch (e) {
      setAviso(e instanceof Error ? e.message : 'No se pudo descargar.');
    } finally {
      setProgreso(null);
    }
  };

  return (
    <>
      {aviso &&
        (soloIconos ? (
          <span role="status" className="absolute bottom-full right-3 mb-2 max-w-[80%] truncate rounded-full bg-brand-black py-1.5 px-3 text-[12.5px] font-medium text-white shadow-lg">
            {aviso}
          </span>
        ) : (
          <span className="max-w-[220px] truncate text-[13px] text-[#B42318]" title={aviso}>
            {aviso}
          </span>
        ))}
      <button type="button" onClick={() => void descargar()} disabled={!!progreso} className={soloIconos ? BOTON_ICONO : BOTON} title={titulo} aria-label={soloIconos ? titulo : undefined}>
        {progreso ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Download className={soloIconos ? 'h-5 w-5' : 'h-4 w-4'} strokeWidth={2} aria-hidden="true" />}
        {!soloIconos && (progreso ? `${progreso.hechos}/${progreso.total}` : 'Descargar')}
      </button>
    </>
  );
};
